import { useReducer } from 'react';

import { Filter } from '../types';

/**
 * Тип статуса, который может иметь автозаполнение.
 * - 'idle': Автозаполнение находится в состоянии покоя.
 * - 'loading': Выполняется запрос за элементами автозаполнения.
 * - 'complete': Запрос завершился успешно, элементы получены.
 * - 'failed': Запрос завершился с ошибкой.
 */
type Status = 'idle' | 'loading' | 'complete' | 'failed';

/**
 * Интерфейс состояния автозаполнения.
 *
 * @template T - Тип элементов, используемых в автозаполнении.
 * @property {Status} status - Текущий статус автозаполнения.
 * @property {T[]} items - Массив текущих элементов автозаполнения.
 * @property {boolean} hasMore - Флаг, указывающий на наличие дополнительных элементов.
 */
interface State<T> {
  status: Status;
  items: T[];
  hasMore: boolean;
  abortedReqIds: symbol[];
  query: string;
}

/**
 * Перечисление типов действий для управления состоянием автозаполнения.
 *
 * @enum {string}
 * @property {string} setStatus - Действие для установки статуса.
 * @property {string} setItems - Действие для установки элементов.
 * @property {string} addItems - Действие для добавления элементов к текущему массиву.
 * @property {string} setHasMore - Действие для установки флага о наличии дополнительных элементов.
 * @property {string} resetState - Действие для сброса состояния к начальному.
 */
enum Actions {
  setQuery = 'setQuery',
  setStatus = 'setStatus',
  setItems = 'setItems',
  addItems = 'addItems',
  setError = 'setError',
  setHasMore = 'setHasMore',
  resetState = 'resetState',
  abortRequest = 'abortRequest',
}
type SetQueryAction = {
  type: Actions.setQuery;
  payload: string;
};

type SetStatusAction = {
  type: Actions.setStatus;
  payload: {
    status: Status;
    reqId: symbol;
  };
};

type SetItemsAction<T> = {
  type: Actions.setItems | Actions.addItems;
  payload: {
    items: T[];
    reqId: symbol;
  };
};

type SetHasMoreAction = {
  type: Actions.setHasMore;
  payload: {
    hasMore: boolean;
    reqId: symbol;
  };
};

type ResetStateAction = {
  type: Actions.resetState;
  payload?: never;
};

type AbortRequestAction = {
  type: Actions.abortRequest;
  payload: symbol;
};

type AutocompleteAction<T> =
  | SetQueryAction
  | SetStatusAction
  | SetHasMoreAction
  | ResetStateAction
  | AbortRequestAction
  | SetItemsAction<T>;

/**
 * Хук useAutocompleteReducer используется для управления состоянием автозаполнения
 * в компонентах ввода. Он предоставляет функции для установки статуса, элементов
 * и флага о наличии дополнительных элементов, а также для выполнения запросов за элементами
 * и сброса состояния.
 *
 * @template T - Тип элементов, используемых в автозаполнении.
 * @param {(filter: Filter) => Promise<T[]>} [callback] - Асинхронная функция, которая будет вызываться
 * для получения элементов автозаполнения на основе переданного фильтра.
 *
 * @returns {Object} Объект с текущим состоянием и функциями для управления им.
 * @returns {Status} state.status - Текущий статус ('idle', 'loading', 'complete', 'failed').
 * @returns {T[]} state.items - Массив текущих элементов автозаполнения.
 * @returns {boolean} state.hasMore - Флаг, указывающий на наличие дополнительных элементов.
 * @returns {(payload: Status) => Action<Status>} setStatus - Функция для установки статуса.
 * @returns {(payload: T[]) => Action<T[]>} setItems - Функция для установки элементов.
 * @returns {(payload: T[]) => Action<T[]>} addItems - Функция для добавления элементов к текущему массиву.
 * @returns {(payload: boolean) => Action<boolean>} setHasMore - Функция для установки флага о наличии дополнительных элементов.
 * @returns {() => Action} resetState - Функция для сброса состояния к начальному.
 * @returns {(filter: Filter, type: 'get' | 'search') => Promise<T[]> | undefined} requestItems - Функция для выполнения запроса за элементами.
 * @returns {() => void} resetItems - Функция для сброса состояния.
 *
 */

export const useAutocompleteReducer = <T>(
  callback?: (filter: Filter) => Promise<T[]>,
  doNotRepeatSimilarRequest = false,
) => {
  const initialState: State<T> = {
    status: 'idle',
    items: [],
    hasMore: true,
    abortedReqIds: [],
    query: '',
  };

  const reducer = (
    state: State<T>,
    { type, payload }: AutocompleteAction<T>,
  ): State<T> => {
    const isActionCancelled = (reqId: symbol) => {
      return state.abortedReqIds.includes(reqId);
    };

    switch (type) {
      case Actions.setQuery:
        return {
          ...state,
          query: payload,
        };

      case Actions.setStatus:
        return isActionCancelled(payload.reqId)
          ? {
              ...state,
            }
          : {
              ...state,
              status: payload.status,
            };

      case Actions.setItems:
        return isActionCancelled(payload.reqId)
          ? { ...state }
          : {
              ...state,
              items: payload.items,
            };

      case Actions.addItems:
        return isActionCancelled(payload.reqId)
          ? { ...state }
          : {
              ...state,
              items: [...state.items, ...payload.items],
            };

      case Actions.setHasMore:
        return isActionCancelled(payload.reqId)
          ? { ...state }
          : {
              ...state,
              hasMore: payload.hasMore,
            };

      case Actions.abortRequest:
        return {
          ...state,
          abortedReqIds: [...state.abortedReqIds, payload],
        };

      case Actions.resetState:
        return initialState;

      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const setStatus = (payload: {
    status: Status;
    reqId: symbol;
  }): SetStatusAction => ({
    type: Actions.setStatus,
    payload,
  });

  const setQuery = (payload: string): SetQueryAction => ({
    type: Actions.setQuery,
    payload,
  });

  const setItems = (payload: {
    items: T[];
    reqId: symbol;
  }): SetItemsAction<T> => ({
    type: Actions.setItems,
    payload,
  });

  const addItems = (payload: {
    items: T[];
    reqId: symbol;
  }): SetItemsAction<T> => ({
    type: Actions.addItems,
    payload,
  });

  const setHasMore = (payload: {
    hasMore: boolean;
    reqId: symbol;
  }): SetHasMoreAction => ({
    type: Actions.setHasMore,
    payload,
  });

  const abortRequest = (payload: symbol) =>
    dispatch({
      type: Actions.abortRequest,
      payload,
    });

  const resetState = (): ResetStateAction => ({
    type: Actions.resetState,
  });

  const requestItemsInner = async (
    filter: Filter,
    type: 'get' | 'search',
    reqId: symbol,
  ) => {
    if (doNotRepeatSimilarRequest) {
      if (state.query === filter.query && state.status === 'complete') {
        return state.items;
      }
    }
    try {
      dispatch(setQuery(filter.query));

      const items = (await callback?.(filter)) || [];

      if (type === 'get') {
        dispatch(addItems({ items, reqId }));
      } else {
        dispatch(setItems({ items, reqId }));
      }

      dispatch(setHasMore({ hasMore: items.length >= filter.limit, reqId }));
      dispatch(setStatus({ status: 'complete', reqId }));
    } catch (err) {
      dispatch(setStatus({ status: 'failed', reqId }));
    }
  };

  const requestItems = (filter: Filter, type: 'get' | 'search') => {
    const reqId = Symbol('reqId');

    dispatch(setStatus({ status: 'loading', reqId }));
    requestItemsInner(filter, type, reqId);

    return () => abortRequest(reqId);
  };

  const resetItems = () => dispatch(resetState());

  return {
    ...state,
    requestItems: callback ? requestItems : undefined,
    resetItems,
  };
};
